import { LazyLoadImage } from 'react-lazy-load-image-component';

// ----------------------------------------------------------------------
// MUI sx tem duas regras usadas pelos ~15 chamadores deste componente:
//   - width/height numero <= 1 vira fracao de 100% (ex.: `width: 1` = 100%);
//     numero > 1 vira pixel puro.
//   - mr/ml/mt/mb multiplicam por 8px (theme.spacing(1)).
// Sem tema MUI para resolver isso, replicamos so essas duas regras - e so
// para as chaves realmente vistas no grep (width, height, zIndex, objectFit,
// mr/ml/mt/mb). O resto do sx e ignorado em silencio.
function comoDimensao(v) {
  if (typeof v !== 'number') return v;
  return v <= 1 ? `${v * 100}%` : v;
}
function comoEspaco(v) {
  return typeof v === 'number' ? v * 8 : v;
}

function sxParaEstilo(sx) {
  // Array de sx (`sx={[{...}, cond && {...}]}`) e usado por 1 chamador fora
  // deste lote (HomeMinimal.jsx) com um `filter` que depende de `theme` -
  // impossivel de resolver aqui de qualquer forma. O codigo MUI anterior ja
  // descartava esse caso por completo (`...(Array.isArray(sx) ? {} : sx)`,
  // um spread manual que nunca soube mesclar array); mantido identico para
  // nao mudar o que aquele chamador ja recebia em producao.
  if (!sx || typeof sx !== 'object' || Array.isArray(sx)) return {};
  const estilo = {};
  if (sx.width !== undefined) estilo.width = comoDimensao(sx.width);
  if (sx.height !== undefined) estilo.height = comoDimensao(sx.height);
  if (sx.zIndex !== undefined) estilo.zIndex = sx.zIndex;
  if (sx.objectFit !== undefined) estilo.objectFit = sx.objectFit;
  if (sx.mr !== undefined) estilo.marginRight = comoEspaco(sx.mr);
  if (sx.ml !== undefined) estilo.marginLeft = comoEspaco(sx.ml);
  if (sx.mt !== undefined) estilo.marginTop = comoEspaco(sx.mt);
  if (sx.mb !== undefined) estilo.marginBottom = comoEspaco(sx.mb);
  return estilo;
}

export default function Image({ ratio = '', disabledEffect = false, effect = 'blur', sx = null, ...other }) {
  // Chamadores ja migrados para o visual novo passam `style` direto (nao
  // `sx`) - ex. UploadAvatar.jsx, login-unprotected.jsx. Se so fizermos
  // spread de `other` depois do nosso `style` default, o `style` do chamador
  // SUBSTITUI o objeto inteiro (spread de objeto nao faz merge de CSS por
  // propriedade) e o Image perde width/height/objectFit - o icone/avatar
  // fica gigante ou desproporcional. Por isso separamos o `style` do
  // chamador e fazemos o merge na mao, na ordem certa.
  const { style: estiloChamador, ...restante } = other;
  const safeSrc = typeof restante.src === 'string' ? restante.src.trim() : restante.src;
  const imageProps = {
    ...restante,
    src: safeSrc || '/images/gps/img_placeholder.svg',
  };

  const estiloExtra = sxParaEstilo(sx);

  if (ratio) {
    return (
      <span
        style={{
          width: '100%',
          lineHeight: 0,
          display: 'block',
          overflow: 'hidden',
          position: 'relative',
          paddingTop: getRatio(ratio),
          ...estiloExtra,
        }}
      >
        {/* Selecionar o span interno do react-lazy-load-image-component exige
            um seletor CSS (ele nao expoe ref nem prop de estilo separada pro
            wrapper) - por isso CSS simples aqui, igual ao `& .wrapper` que
            existia dentro do sx do MUI antes. */}
        <style>{`
          .gps-image-wrap-ratio { position: absolute; inset: 0; line-height: 0; background-size: contain !important; }
        `}</style>
        <LazyLoadImage
          wrapperClassName="gps-image-wrap-ratio"
          effect={disabledEffect ? undefined : effect}
          placeholderSrc="/images/gps/img_placeholder.svg"
          {...imageProps}
          style={{ width: '100%', height: '100%', objectFit: 'contain', ...estiloChamador }}
        />
      </span>
    );
  }

  return (
    <span
      style={{
        lineHeight: 0,
        display: 'block',
        overflow: 'hidden',
        objectFit: 'contain',
        ...estiloExtra,
      }}
    >
      <style>{`
        .gps-image-wrap-plain { width: 100%; height: 100%; background-size: contain !important; }
      `}</style>
      <LazyLoadImage
        wrapperClassName="gps-image-wrap-plain"
        effect={disabledEffect ? undefined : effect}
        placeholderSrc="/images/gps/img_placeholder.svg"
        {...imageProps}
        style={{ width: '100%', height: '100%', objectFit: 'contain', ...estiloChamador }}
      />
    </span>
  );
}

// ----------------------------------------------------------------------

function getRatio(ratio = '1/1') {
  return {
    '4/3': 'calc(100% / 4 * 3)',
    '3/4': 'calc(100% / 3 * 4)',
    '6/4': 'calc(100% / 6 * 4)',
    '4/6': 'calc(100% / 4 * 6)',
    '16/9': 'calc(100% / 16 * 9)',
    '9/16': 'calc(100% / 9 * 16)',
    '21/9': 'calc(100% / 21 * 9)',
    '9/21': 'calc(100% / 9 * 21)',
    '1/1': '100%',
  }[ratio];
}
